// src/search/offlineAddressSearchResponse.ts
import Dexie from "dexie";
import { db } from "@/db";
import { formatNoteForDisplay, statusDisplay } from "@/offline/noteUtils";
import { NONE_VALUE, NO_STATUS_VALUE } from "./constants";

type OfflineSearchParams = {
  street: string;
  postcode: string;
  postcode_facet?: string[];
  post_town_facet?: string[];
  dependent_locality_facet?: string[];
  status_facet?: string[];
  status?: string[];
  page?: number;
  page_size?: number;
};

function normalizePostcode(postcode: string) {
  return postcode.replace(/\s+/g, "").toUpperCase();
}

function normalizeStatusValue(status?: string | null) {
  if (!status || status === NO_STATUS_VALUE) return NO_STATUS_VALUE;
  return status;
}

function normalizeFacetValue(value?: string | null) {
  const trimmed = value?.trim() ?? "";
  return trimmed === "" ? NONE_VALUE : trimmed;
}

function toCountArray(counts: Map<string, number>) {
  return Array.from(counts.entries())
    .map(([value, count]) => ({ value, count }))
    .sort((a, b) => b.count - a.count);
}

function deriveQueuedSortTs(n: any) {
  return String(
    n?.created_at_client ??
    (n as any)?.created_at ??
    n?.payload?.body?.client_created_at ??
    ""
  );
}

const normalizeSortSecondary = (v: any) => {
  if (v === null || v === undefined) return "";
  return String(v);
};

const normalizeSortNumber = (v: any) => {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

export async function offlineAddressSearchResponse({
  street,
  postcode,
  postcode_facet = [],
  post_town_facet = [],
  dependent_locality_facet = [],
  status_facet = [],
  status = [],
  page = 1,
  page_size = 200,
}: OfflineSearchParams) {
  const normalizedPostcode = postcode ? normalizePostcode(postcode) : "";
  const normalisedThoroughfare = street ? street.trim().toLowerCase() : "";

  // -------------------------
  // 1) Base query using index
  // -------------------------
  let collection: Dexie.Collection<any, number> | null = null;

  if (normalizedPostcode) {
    collection = db.addresses
      .where("[thoroughfare+postcode_normalised]")
      .between(
        [Dexie.minKey, normalizedPostcode],
        [Dexie.maxKey, `${normalizedPostcode}\uffff`]
      );
  } else if (normalisedThoroughfare) {
    collection = db.addresses
      .where("thoroughfare")
      .startsWithIgnoreCase(normalisedThoroughfare);
  }

  if (!collection) {
    return {
      results: [],
      postcode_counts: [],
      post_town_counts: [],
      dependent_locality_counts: [],
      status_counts: [],
      count: 0,
      pre_facet_count: 0,
      total_pages: 1,
      previous: null,
      next: null,
      offline: true,
    };
  }

  const baseMatches = await collection.toArray();

  const textMatched = baseMatches.filter((addr) => {
    if (normalisedThoroughfare) {
      const t = (addr.thoroughfare ?? "").toLowerCase();
      if (!t.startsWith(normalisedThoroughfare)) return false;
    }

    if (normalizedPostcode) {
      const p = normalizePostcode(addr.postcode_normalised ?? "");
      if (!p.startsWith(normalizedPostcode)) return false;
    }
    return true;
  });

  if (!textMatched.length) {
    return {
      results: [],
      postcode_counts: [],
      post_town_counts: [],
      dependent_locality_counts: [],
      status_counts: [],
      count: 0,
      pre_facet_count: 0,
      total_pages: 1,
      previous: null,
      next: null,
      offline: true,
    };
  }

  const udprns = textMatched.map((a) => a.udprn);

  const [addressStates, syncedNotes, queuedNotes] = await Promise.all([
    db.addressStates.where("udprn").anyOf(udprns).toArray(),
    db.notesSynced.where("udprn").anyOf(udprns).toArray(),
    db.notesQueue.where("udprn").anyOf(udprns).toArray(),
  ]);

  const stateStatusMap = new Map<number, string | null>();
  addressStates.forEach((s: any) => {
    stateStatusMap.set(s.udprn, s.status ?? null);
  });

  const latestSyncedMap = new Map<number, any>();
  syncedNotes.forEach((n) => {
    const existing = latestSyncedMap.get(n.udprn);
    if (!existing || (existing.created_at ?? "") < (n.created_at ?? "")) {
      latestSyncedMap.set(n.udprn, n);
    }
  });

  const latestQueuedMap = new Map<number, any>();
  queuedNotes.forEach((n) => {
    const ts = deriveQueuedSortTs(n);
    const existing = latestQueuedMap.get(n.udprn);
    if (!existing || (existing.sort_ts ?? "") < ts) {
      latestQueuedMap.set(n.udprn, { ...n, sort_ts: ts });
    }
  });

  const deriveStatus = (udprn: number) => {
    const queued = latestQueuedMap.get(udprn);
    const synced = latestSyncedMap.get(udprn);
    const stateStatus = stateStatusMap.get(udprn) ?? null;
    const queuedStatus =
      queued?.payload?.body?.status ?? queued?.status ?? null;
    const syncedStatus = synced?.status ?? null;
    return queuedStatus ?? syncedStatus ?? stateStatus ?? null;
  };

  const annotated = textMatched.map((addr) => ({
    ...addr,
    latest_status: deriveStatus(addr.udprn),
  }));

  // -----------------------------------
  // 2) Apply primary status filter (UI)
  // -----------------------------------
  const statusFilterSet = new Set(
    (status ?? []).map((s) => normalizeStatusValue(s)).filter(Boolean)
  );

  const statusFiltered = annotated.filter((addr) => {
    if (!statusFilterSet.size) return true;
    const statusValue = normalizeStatusValue(addr.latest_status);
    return statusFilterSet.has(statusValue);
  });

  const pre_facet_count = statusFiltered.length;

  // -------------------------
  // 3) Build facet count data
  // -------------------------
  const postcodeCounts = new Map<string, number>();
  const postTownCounts = new Map<string, number>();
  const localityCounts = new Map<string, number>();
  const statusCounts = new Map<string, number>();

  statusFiltered.forEach((addr) => {
    const postcodeValue = normalizePostcode(addr.postcode_normalised ?? "");
    if (postcodeValue) {
      postcodeCounts.set(postcodeValue, (postcodeCounts.get(postcodeValue) ?? 0) + 1);
    }

    const postTownValue = normalizeFacetValue((addr as any).post_town);
    postTownCounts.set(postTownValue, (postTownCounts.get(postTownValue) ?? 0) + 1);

    const localityValue = normalizeFacetValue((addr as any).dependent_locality);
    localityCounts.set(localityValue, (localityCounts.get(localityValue) ?? 0) + 1);

    const statusValue = normalizeStatusValue(addr.latest_status);
    statusCounts.set(statusValue, (statusCounts.get(statusValue) ?? 0) + 1);
  });

  // -------------------------
  // 4) Apply facet filtering
  // -------------------------
  const postcodeFacetSet = new Set(
    (postcode_facet ?? []).map((p) => normalizePostcode(p)).filter(Boolean)
  );
  const postTownFacetSet = new Set(
    (post_town_facet ?? []).map((p) => normalizeFacetValue(p))
  );
  const localityFacetSet = new Set(
    (dependent_locality_facet ?? []).map((p) => normalizeFacetValue(p))
  );
  const statusFacetSet = new Set(
    (status_facet ?? []).map((p) => normalizeStatusValue(p))
  );

  const faceted = statusFiltered.filter((addr) => {
    const postcodeValue = normalizePostcode(addr.postcode_normalised ?? "");
    if (postcodeFacetSet.size && !postcodeFacetSet.has(postcodeValue)) return false;

    const postTownValue = normalizeFacetValue((addr as any).post_town);
    if (postTownFacetSet.size && !postTownFacetSet.has(postTownValue)) return false;

    const localityValue = normalizeFacetValue((addr as any).dependent_locality);
    if (localityFacetSet.size && !localityFacetSet.has(localityValue)) return false;

    const statusValue = normalizeStatusValue(addr.latest_status);
    if (statusFacetSet.size && !statusFacetSet.has(statusValue)) return false;

    return true;
  });

  // Determine which facets to expose based on the un-faceted option breadth,
  // but always include currently selected facets.
  const allowPostcodeFacet =
    postcodeCounts.size > 1 || (postcodeFacetSet.size > 0 && postcodeCounts.size > 0);
  const allowPostTownFacet =
    postTownCounts.size > 1 || (postTownFacetSet.size > 0 && postTownCounts.size > 0);
  const allowLocalityFacet =
    localityCounts.size > 1 || (localityFacetSet.size > 0 && localityCounts.size > 0);
  const allowStatusFacet =
    statusCounts.size > 1 || (statusFacetSet.size > 0 && statusCounts.size > 0);

  // Recompute counts after faceting so the UI reflects the filtered set.
  const filteredPostcodeCounts = new Map<string, number>();
  const filteredPostTownCounts = new Map<string, number>();
  const filteredLocalityCounts = new Map<string, number>();
  const filteredStatusCounts = new Map<string, number>();

  faceted.forEach((addr) => {
    const postcodeValue = normalizePostcode(addr.postcode_normalised ?? "");
    if (postcodeValue) {
      filteredPostcodeCounts.set(
        postcodeValue,
        (filteredPostcodeCounts.get(postcodeValue) ?? 0) + 1
      );
    }

    const postTownValue = normalizeFacetValue((addr as any).post_town);
    filteredPostTownCounts.set(
      postTownValue,
      (filteredPostTownCounts.get(postTownValue) ?? 0) + 1
    );

    const localityValue = normalizeFacetValue((addr as any).dependent_locality);
    filteredLocalityCounts.set(
      localityValue,
      (filteredLocalityCounts.get(localityValue) ?? 0) + 1
    );

    const statusValue = normalizeStatusValue(addr.latest_status);
    filteredStatusCounts.set(
      statusValue,
      (filteredStatusCounts.get(statusValue) ?? 0) + 1
    );
  });

  // -------------------------
  // 5) Sort & limit
  // -------------------------
  const sorted = faceted.sort((a, b) => {
    const t = (a.thoroughfare ?? "").localeCompare(
      b.thoroughfare ?? "",
      undefined,
      { sensitivity: "base" }
    );
    if (t !== 0) return t;

    const snA = normalizeSortNumber((a as any).sort_number);
    const snB = normalizeSortNumber((b as any).sort_number);
    if (snA !== snB) return snA - snB;

    return normalizeSortSecondary((a as any).sort_secondary).localeCompare(
      normalizeSortSecondary((b as any).sort_secondary),
      undefined,
      { sensitivity: "base" }
    );
  });

  const totalPages = Math.max(1, Math.ceil(faceted.length / page_size));
  const currentPage = Math.min(Math.max(1, page), totalPages);
  const start = (currentPage - 1) * page_size;
  const limitedResults = sorted.slice(start, start + page_size);
  const limitedUdprns = limitedResults.map((a) => a.udprn);

  const postcode_counts = allowPostcodeFacet ? toCountArray(filteredPostcodeCounts).slice(0, 20) : [];
  const post_town_counts = allowPostTownFacet ? toCountArray(filteredPostTownCounts) : [];
  const dependent_locality_counts = allowLocalityFacet ? toCountArray(filteredLocalityCounts) : [];
  const status_counts = allowStatusFacet
    ? toCountArray(filteredStatusCounts).map((s) => ({
        ...s,
        label: statusDisplay(
          s.value === NO_STATUS_VALUE ? null : s.value,
          s.value === NO_STATUS_VALUE ? "No status" : undefined
        ),
      }))
    : [];

  if (!limitedUdprns.length) {
    return {
      results: [],
      postcode_counts,
      post_town_counts,
      dependent_locality_counts,
      status_counts,
      pre_facet_count,
      count: faceted.length,
      total_pages: totalPages,
      previous: currentPage > 1 ? String(currentPage - 1) : null,
      next: currentPage < totalPages ? String(currentPage + 1) : null,
      offline: true,
    };
  }

  // -------------------------
  // 6) Hydrate customers/notes
  // -------------------------
  const [customers] = await Promise.all([
    db.customers.where("udprn").anyOf(limitedUdprns).toArray(),
  ]);

  const customerMap = new Map<number, any>();
  customers.forEach((c) => customerMap.set(c.udprn, c));

  const results = limitedResults.map((addr) => {
    const queued = latestQueuedMap.get(addr.udprn);
    const synced = latestSyncedMap.get(addr.udprn);

    const latestQueued = queued
      ? formatNoteForDisplay({
          ...(queued.payload?.body ?? {}),
          pending: true,
          created_at: queued.created_at_client ?? queued.sort_ts,
          created_at_client: queued.created_at_client ?? queued.sort_ts,
          username: queued.payload?.body?.username ?? "You (offline)",
        })
      : null;

    const latestSynced = synced ? formatNoteForDisplay(synced) : null;

    return {
      ...addr,
      customer: customerMap.get(addr.udprn) ?? null,
      latest_note: latestQueued ?? latestSynced,
    };
  });

  return {
    results,
    postcode_counts,
    post_town_counts,
    dependent_locality_counts,
    status_counts,
    pre_facet_count,
    count: faceted.length,
    total_pages: totalPages,
    previous: currentPage > 1 ? String(currentPage - 1) : null,
    next: currentPage < totalPages ? String(currentPage + 1) : null,
    offline: true,
  };
}
